import { Box, Flex, SimpleGrid } from "@chakra-ui/react";
import { ProductImageCarousel } from "@components/Carousel/ProductImageCarousel";
import { ImageCommonComponent } from "@components/Common/Image";
import { Container } from "@components/Container";
import { ProductAdvantages } from "@components/Products/ProductDetails/ProductAdvantages/ProductAdvantages";
import { ProductButtons } from "@components/Products/ProductDetails/ProductButtons";
import { ProductDetailsHeader } from "@components/Products/ProductDetails/ProductDetailsHeader";
import { ProductSoftness } from "@components/Products/ProductDetails/ProductSoftness";
import { TableDetails } from "@components/Products/ProductDetails/TableDetails/TableDetails";
import * as Sentry from "@sentry/nextjs";
import { getLayoutDefaultProps, LayoutData, LayoutDefault } from "@src/layouts";
import { getFirmnessLevels } from "@src/service/firmness";
import { getProduct } from "@src/service/products";
import { Firmness, Product } from "@src/types/strapi";
import { makeProductAdvantages } from "@src/utils/make-product-advantages";
import { StrapiYoutubeSection } from '@src/types/strapi/components/youtube-section'
import { YoutubeSection } from '@components/Sections/Youtube'


interface ProductDetailsPageProps extends LayoutData {
  product: Product;
  firmness: Firmness[];
  youtubeSection: StrapiYoutubeSection
}

export default function ProductDetailsPage({ product, firmness, youtubeSection, ...layoutProps }: ProductDetailsPageProps) {
  const { advantagesPriority, advantagesNormal } = makeProductAdvantages(product.advantages);

  return (
    <LayoutDefault
      title={product.title}
      description={product.shortDescription}
      pageKey={"product-details"}
      {...layoutProps}
    >
      <Flex flex={1} flexDirection="column" gridGap={{ base: "1rem", sm: "2rem", md: "3rem", lg: "6rem" }}>
        <Flex flexDirection={"column"} gridGap={{ base: "1rem", md: "3rem", lg: "5rem", xl: "9.5rem" }}>
          <ProductDetailsHeader
            title={product.title}
            purchaseLink={product.purchaseLink}
            baseCode={product.baseCode}
            code={product.code}
            description={product.description}
            highLightImage={product.highlightImage?.desktop_image?.url}
            category={product.category?.title}
            advantages={advantagesPriority}
          />
          {product.images?.length && <ProductImageCarousel items={product.images} />}
        </Flex>
        {advantagesNormal?.length && <ProductAdvantages advantages={advantagesNormal} />}
        <Container flexDirection={"column"} gridGap={{ base: "2rem", md: "4rem" }}>
          {product.firmness && <ProductSoftness softness={firmness} currentSoftnessId={product.firmness.id} />}
          {product.layers && (
            <Box position={"relative"} height={{ base: "250px", md: "400px", lg: "500px", xl: "542px" }} width="full">
              <ImageCommonComponent {...product.layers} />
            </Box>
          )}
          <ProductButtons purchaseLink={product.purchaseLink} />
          {youtubeSection && <YoutubeSection {...youtubeSection} />}
        </Container>
        {product.productDescriptionDetails?.length && (
          <TableDetails
            title="Mais detalhes"
            details={product.productDescriptionDetails.map((item) => ({
              name: item.label,
              value: item.value,
            }))}
          />
        )}
        <Container
          flexDirection={"column"}
          gridGap={{ base: "2rem", md: "4rem" }}
          padding="0px"
          marginBottom={{
            base: "2rem",
            md: "4rem",
            lg: "6rem",
            xl: "9.625rem",
          }}
        >
          {product.productDimensionDetails?.length && (
            <TableDetails
              title="Medidas"
              details={product.productDimensionDetails.map((item) => ({
                name: item.label,
                value: item.value,
              }))}
            />
          )}
          {product.footerImages?.length && (
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              gridGap={0}
              width="full"
              paddingX={{ base: "1.8125rem", lg: "0px" }}
            >
              {product.footerImages.map((image) => (
                <Box key={image.id}>
                  <ImageCommonComponent
                    key={image.id}
                    {...image}
                    imageProps={{
                      layout: "responsive",
                      width: "575px",
                      height: "333px",
                    }}
                  />
                </Box>
              ))}
            </SimpleGrid>
          )}
          <ProductButtons purchaseLink={product.purchaseLink} />
        </Container>
      </Flex>
    </LayoutDefault>
  );
}

export async function getServerSideProps(context) {
  try {
    const productId = Number(context.params.slug.split("-").pop());

    const [layoutProps, product, firmness] = await Promise.all([
      getLayoutDefaultProps(),
      getProduct(productId),
      getFirmnessLevels(),
    ]);

    return {
      props: {
        ...layoutProps,
        product,
        firmness,
        youtubeSection: product.Youtube,
      },
    };
  } catch (error) {
    Sentry.captureException(error);

    return {
      notFound: true,
    };
  }
}
